import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const In = ({ label, value, onChange, type = "text", testId, disabled }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <input type={type} value={value} disabled={disabled} data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      className={`glass-input ${disabled ? "opacity-60 cursor-not-allowed" : ""}`} />
  </div>
);

export default function AccountSettingsDialog({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ name: "", email: "", mobile: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoTick, setPhotoTick] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && user) {
      setF({ name: user.name || "", email: user.email || "", mobile: user.mobile || "" });
      setPw({ current_password: "", new_password: "", confirm_password: "" });
      setPhoto(null);
      if (user.has_photo) {
        api.get("/auth/photo", { responseType: "blob" })
          .then((r) => setPhoto(URL.createObjectURL(r.data)))
          .catch(() => setPhoto(null));
      }
    }
  }, [open, photoTick, user]);

  if (!user) return null;
  const initials = (user.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async () => {
    if (!f.name.trim()) { toast.error("Name is required."); return; }
    if (!f.email.trim()) { toast.error("Email is required."); return; }
    setBusy(true);
    try {
      await api.patch("/auth/profile", { name: f.name.trim(), email: f.email.trim(), mobile: f.mobile.trim() || null });
      await refresh();
      toast.success("Profile updated — changes are audit logged.");
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Enter current and new password."); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error("New password and confirmation do not match."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed securely — audit logged.");
      setPw({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPwBusy(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/auth/photo", fd);
      await refresh();
      setPhotoTick((k) => k + 1);
      toast.success("Profile photo updated");
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove profile photo?")) return;
    try {
      await api.delete("/auth/photo");
      await refresh();
      setPhoto(null);
      toast.success("Profile photo removed");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="account-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Account Settings</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Manage your administrator account. All changes are recorded in Audit Logs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-7">
          <div>
            <p className="label-caps mb-3">Profile</p>
            <div className="flex items-center gap-4 mb-5">
              {photo ? (
                <img src={photo} alt={user.name} data-testid="as-photo-preview"
                  className="h-16 w-16 rounded-full object-cover border border-emerald-500/25" />
              ) : (
                <div data-testid="as-photo-preview"
                  className="h-16 w-16 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 flex items-center justify-center font-display text-xl">
                  {initials}
                </div>
              )}
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                  data-testid="as-photo-input" onChange={uploadPhoto} />
                <Button size="sm" variant="outline" data-testid="as-photo-upload"
                  onClick={() => fileRef.current?.click()} className="border-white/10 text-zinc-300 hover:bg-white/5">
                  Upload / Replace
                </Button>
                {user.has_photo && (
                  <Button size="sm" variant="outline" data-testid="as-photo-remove" onClick={removePhoto}
                    className="border-red-500/25 text-red-400 hover:bg-red-500/10">
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <In label="Full Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="as-name" />
              <In label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="as-email" />
              <In label="Mobile" value={f.mobile} onChange={(v) => setF({ ...f, mobile: v })} testId="as-mobile" />
              <In label="Login ID" value={user.username || "—"} onChange={() => {}} disabled testId="as-login-id" />
            </div>
            <div className="flex gap-3 mt-5">
              <Button onClick={saveProfile} disabled={busy} data-testid="as-save"
                className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
                {busy ? "Saving..." : "Save Changes"}
              </Button>
              <Button variant="outline" onClick={onClose} data-testid="as-cancel"
                className="border-white/10 text-zinc-300 hover:bg-white/5">
                Cancel
              </Button>
            </div>
          </div>

          <div className="border-t border-white/[0.06] pt-6">
            <p className="label-caps mb-1">Security — Change Password</p>
            <p className="text-[11px] text-zinc-600 mb-4">Your current password is never displayed. Minimum 8 characters with at least one letter and one number.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <In label="Current Password" type="password" value={pw.current_password}
                onChange={(v) => setPw({ ...pw, current_password: v })} testId="as-current-password" />
              <In label="New Password" type="password" value={pw.new_password}
                onChange={(v) => setPw({ ...pw, new_password: v })} testId="as-new-password" />
              <In label="Confirm New Password" type="password" value={pw.confirm_password}
                onChange={(v) => setPw({ ...pw, confirm_password: v })} testId="as-confirm-password" />
            </div>
            <Button onClick={changePassword} disabled={pwBusy} data-testid="as-change-password"
              className="mt-4 bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
              {pwBusy ? "Updating..." : "Change Password"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
